import React, { useState } from 'react';

// Clean, minimal styles
const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '1.5rem',
  transition: 'all 0.2s ease',
  cursor: 'pointer'
};

const cardHoverStyle = {
  borderColor: '#3182ce',
  boxShadow: '0 4px 12px rgba(49, 130, 206, 0.15)'
};

const tagStyle = {
  display: 'inline-block',
  background: '#f7fafc',
  color: '#2d3748',
  padding: '4px 12px',
  borderRadius: '16px',
  fontSize: '0.75rem',
  fontWeight: '500',
  margin: '2px 4px 2px 0',
  border: '1px solid #e2e8f0'
};

const iconStyle = {
  fontSize: '1.5rem',
  color: '#3182ce',
  marginBottom: '0.75rem'
};



const titleStyle = {
  fontSize: '1.25rem',
  fontWeight: '600',
  color: '#2d3748',
  marginBottom: '0.5rem'
};

const descriptionStyle = {
  fontSize: '0.95rem',
  lineHeight: '1.5',
  color: '#4a5568',
  marginBottom: '1rem'
};

// Simple Use Case Card Component
const UseCaseCard = ({ icon, title, description, features, id, isHovered, onHover, onLeave }) => {
  const currentCardStyle = {
    ...cardStyle,
    ...(isHovered ? cardHoverStyle : {})
  };

  return (
    <div 
      style={currentCardStyle}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      id={id}
    >
      <div style={iconStyle}>
        <i className={icon}></i>
      </div>
      
      <h3 style={titleStyle}>{title}</h3>
      <p style={descriptionStyle}>{description}</p>
      
      <div>
        {features.map((feature, index) => (
          <span key={index} style={tagStyle}>{feature}</span>
        ))}
      </div>
    </div>
  );
};

const UseCases = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const useCaseData = [
    {
      id: 'ai-roleplay',
      icon: 'fa-solid fa-masks-theater',
      title: 'AI Role Play',
      description: 'Create immersive role-playing experiences with persistent character memories.',
      features: ['Character Memory', 'Story Continuity']
    },
    {
      id: 'ai-education',
      icon: 'fa-solid fa-graduation-cap',
      title: 'AI Education',
      description: 'Develop adaptive learning systems that track student progress and preferences.',
      features: ['Progress Tracking', 'Adaptive Learning']
    },
    {
      id: 'ai-therapy',
      icon: 'fa-solid fa-heart',
      title: 'AI Therapy',
      description: 'Build supportive therapy bots that remember session history and patient needs.',
      features: ['Session Memory', 'Patient Care']
    },
    {
      id: 'ai-robot',
      icon: 'fa-solid fa-cog',
      title: 'AI Robot',
      description: 'Enable robots to learn from interactions and adapt to environment changes.',
      features: ['Environmental Learning', 'Interaction Memory']
    },
    {
      id: 'ai-creation',
      icon: 'fa-solid fa-palette',
      title: 'AI Creation',
      description: 'Create AI that remembers artistic styles, preferences, and creative processes.',
      features: ['Style Memory', 'Creative Process']
    }
  ];

  return (
    <div className="doc-content" style={{background: '#f8fafc', minHeight: '100vh', padding: '2rem 1rem'}}>
      {/* Simple Header */}
      <div style={{ textAlign: 'center', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem auto' }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1a202c', marginBottom: '1rem' }}>
          Use Cases
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#4a5568', lineHeight: '1.6' }}>
          Discover how MemU powers various AI applications from education to therapy, role-playing, and creative tools.
        </p>
      </div>
      
      {/* Simple Info Section */}
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '1.5rem',
        margin: '0 auto 3rem auto',
        maxWidth: '800px',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: '1rem', color: '#4a5568', margin: 0, lineHeight: '1.6' }}>
          MemU is designed for <strong>diverse AI applications</strong> that require persistent memory capabilities. 
          From personal assistants to educational tools, MemU adapts to your <strong>specific use case</strong>.
        </p>
      </div>
      
      {/* Clean Use Cases Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1.5rem',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {useCaseData.map((useCase) => (
          <UseCaseCard
            key={useCase.id}
            {...useCase}
            isHovered={hoveredCard === useCase.id}
            onHover={() => setHoveredCard(useCase.id)}
            onLeave={() => setHoveredCard(null)}
          />
        ))}
      </div>
    </div>
  );
};

export default UseCases;